import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Upload,
  message,
  Typography,
} from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useState, useEffect } from "react";

import { ICountry, IJobGrade, IModal } from "@types";
import CustomModal from "@/components/customModel";
import {
  ErrorServices,
  useCreateBenefitsPlanMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchBenefitPlanTypesQuery,
  useFetchBusinessUnitsQuery,
  useFetchCompanyCountriesQuery,
  useUpdateBenefitsPlanMutation,
} from "@services";
import {
  ALL_COUNTRIES,
  ALL_GRADES_VALUE,
  showSuccessPopup,
  sortedByName,
} from "@utils";
import { useTypedSelector } from "@hooks";
import {
  getSelectedCompanyFromStorage,
  getTokenFromSharedStorage,
} from "@/services/cookies";
import customErrorMessage from "@/components/CustomErrorMessage";

const { Option } = Select;
const { Text } = Typography;
interface IAddBenefitsPlan extends IModal {
  selectedbenefitPlan?: any | null;
  setSelectedbenefitPlan?: React.Dispatch<React.SetStateAction<any | null>>;
}
// Format numbers with commas
export const formatNumberWithCommas = (number: any) => {
  if (!number) return "";
  const n = Number(number);
  if (isNaN(n)) return "";
  return n;
};

const INITIAL_BENEFITS_PLAM: any = {
  name: "",
  grade: [],
  country_id: null,
  city: "",
  is_all_grade: false,
  is_global: false,
  value: null,
  description: "",
  category: null,
  // Non-Cash fields
  benefit_description: "",
  benefit_value: null,
  cost_to_employer: null,
  policy_manual: null,
  business_unit_ids: [],
  include_in_offer_detail: false,
  include_in_total_remuneration: false,
};

const AddBenefitsPlan: FC<any> = ({ isVisible, setIsVisible }) => {
  const { company } = useTypedSelector(state => state.auth.user!);
  const { id: company_id } = company!;
  const [loading, setLoading] = useState(false);

  const [benefitPlan, setBenefitPlan] = useState<any>(INITIAL_BENEFITS_PLAM);

  const [createBenefitPlan, { isLoading: isCreating }] =
    useCreateBenefitsPlanMutation();
  const { data: planTypes, isLoading } = useFetchBenefitPlanTypesQuery();

  const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);
  const { data: countriesData } = useFetchCompanyCountriesQuery({ company_id });
  const countries = countriesData?.data || [];

  const { data: jobGradesData } = useFetchAllCompanyJobGradesQuery({
    company_id,
  });
  const jobGrades = jobGradesData?.data || [];

  const { data: businessUnitsData } = useFetchBusinessUnitsQuery({
    company_id,
  });

  const businessUnits = businessUnitsData?.data || [];

  // Handle input & checkbox changes
  // const handleChange = (e: any) => {
  //   const { name, value, checked } = e.target;
  //   setBenefitPlan(prev => ({
  //     ...prev,
  //     [name]: name?.includes("is_") ? checked : value,
  //   }));

  //   if (name === "is_all_grade" && checked) {
  //     setGradeCountries(countries);
  //   }
  // };
  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;

    setBenefitPlan(prev => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name?.includes("is_")
            ? checked
            : value,
    }));

    // Special logic for "All Grades"
    if (name === "is_all_grade" && checked) {
      setGradeCountries(countries);
    }
  };

  // Handle Select changes
  const handleSelectChange = (name: string, value: any) => {
    setBenefitPlan(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGradeChange = (selectedGrades: string[]) => {
    const selectedJobGrades = jobGrades.filter(jg =>
      selectedGrades.includes(jg.grade)
    );
    const mergedCountries =
      selectedJobGrades?.flatMap(jg => jg.countries || []) || [];
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );
    setGradeCountries(uniqueCountries);
    setBenefitPlan(prev => ({
      ...prev,
      grade: selectedGrades,
      country_id: null, // reset selected country if grades change
      is_global: false,
    }));
  };
  const handleGlobalChange = (e: any) => {
    const isChecked = e;

    const allCountryIds = gradeCountries?.map(c => c.id) || [];

    setBenefitPlan((prev: any) => ({
      ...prev,
      is_global: false,
      country_id: isChecked ? allCountryIds : [], // select all or clear
    }));
  };
  const uploadPolicy = async (): Promise<boolean> => {
    if (!benefitPlan?.policy_manual) return false; // no file = allow submit
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("policy_manual", benefitPlan.policy_manual);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/company/${company_id}/upload-policy/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getTokenFromSharedStorage()?.access}`,
            Accept: "application/json",
          },
          body: formData,
        }
      );
      setLoading(false);
      if (!res.ok) {
        throw new Error("Upload failed");
      }

      const data = await res.json();
      return data;
    } catch (error: any) {
      setLoading(false);
      return false;
    }
  };

  // Upload handler
  const handleUpload = (file: any) => {
    setBenefitPlan(prev => ({ ...prev, policy_manual: file }));
    return false; // prevent auto upload
  };

  // Validation
  const isValid = () => {
    const base =
      benefitPlan?.name &&
      benefitPlan?.category &&
      benefitPlan?.benefit_type &&
      gradeCountries.length > 0 &&
      (benefitPlan?.is_all_grade ||
        (benefitPlan?.grade && benefitPlan?.grade?.length > 0)) &&
      benefitPlan?.country_id &&
      benefitPlan?.description;
    if (!base) return false;

    if (benefitPlan?.benefit_type === "cash") {
      return (
        benefitPlan?.calculation_basis &&
        benefitPlan?.value !== null &&
        benefitPlan?.value !== undefined
      );
    } else {
      return (
        benefitPlan?.description &&
        benefitPlan?.value !== null &&
        benefitPlan?.value !== undefined &&
        benefitPlan?.employer_cost !== null &&
        benefitPlan?.employer_cost !== undefined
      );
    }
  };
  // Submit handler
  const handleSubmit = async () => {
    try {
      const isUploaded: any = await uploadPolicy();
      // if (!isUploaded || !benefitPlan?.policy_manual) {
      //   customErrorMessage("Policy is required. Benefit plan not created.");
      //   return;
      // }
      const payload: any = {
        name: benefitPlan?.name,
        benefit_type: benefitPlan?.benefit_type,
        category: benefitPlan?.category,
        grades: benefitPlan?.is_all_grade
          ? Array.from(new Set(jobGrades?.map((jg: any) => jg?.grade)))
          : Array.from(new Set(benefitPlan?.grade || [])),
        is_all_grade: false,
        is_global: false,
        country_ids: benefitPlan?.country_id,
        description: benefitPlan?.description,
        plan_type_id: benefitPlan?.benefit_type === "cash" ? 1 : 2,
        // value: benefitPlan?.value ? +benefitPlan?.value : null,
        value:
          benefitPlan?.value !== null && benefitPlan?.value !== undefined
            ? +benefitPlan.value
            : null,
        city: benefitPlan?.city,
        // employer_cost:
        //   benefitPlan?.benefit_type === "non_cash" && benefitPlan?.employer_cost
        //     ? +benefitPlan.employer_cost
        //     : null,
        employer_cost:
          benefitPlan?.benefit_type === "non_cash" &&
            benefitPlan?.employer_cost !== null &&
            benefitPlan?.employer_cost !== undefined
            ? +benefitPlan.employer_cost
            : null,
        calculation_basis:
          benefitPlan?.benefit_type === "cash" && benefitPlan?.calculation_basis
            ? benefitPlan?.calculation_basis
            : null,
        policy_manual_url: isUploaded?.data?.policy_manual_url
          ? isUploaded?.data?.policy_manual_url
          : null,
        business_unit_ids: benefitPlan?.business_unit_ids || [],
        include_in_offer_detail: benefitPlan?.include_in_offer_detail,
        include_in_total_remuneration: benefitPlan?.include_in_total_remuneration,
      };

      await createBenefitPlan({ company_id, body: payload }).unwrap();
      showSuccessPopup({
        title: "New Benefit Plans Created",
        desc: "You have successfully created the benefit plan.",
        role: "client",
      });

      setIsVisible(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      ErrorServices(error);
    }
  };

  return (
    <CustomModal
      width="w-7/12"
      title={`${"Create"} Benefit Plan`}
      isVisible={isVisible}
      height="80vh"
      footer={
        <>
          <button
            className="bg-themeGrey text-themeBlack font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            {" "}
            <span> Cancel</span>
          </button>
          <button
            className={`bg-themeBlue text-white font-medium text-lg py-2 px-14 rounded-md ${!isValid() && "opacity-50 cursor-auto"}`}
            type="primary"
            disabled={!isValid()}
            onClick={handleSubmit}
          >
            <span>
              {isCreating || loading ? <LoadingOutlined /> : "Create"}
            </span>
          </button>
        </>
      }
      onClose={() => setIsVisible(false)}
    >
      <Row gutter={16}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Name
          </label>
          <Input
            className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
            size="large"
            name="name"
            placeholder="Name of Benefit Plan"
            value={benefitPlan?.name}
            onChange={handleChange}
          />
        </Col>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Category
          </label>
          <Select
            className="custom-select"
            size="large"
            placeholder="Select category"
            value={benefitPlan?.category}
            onChange={value => handleSelectChange("category", value)}
          >
            <Option value="health">Health</Option>
            <Option value="retirement">Retirement</Option>
            <Option value="statutory">Statutory</Option>
            <Option value="wellness">Wellness</Option>
            {/* new options */}
            <Option value="financial">Financial</Option>
            <Option value="lifestyle">Lifestyle</Option>
            <Option value="statutory_time_off">Statutory – Time Off</Option>
            <Option value="statutory_provisions">Statutory – Provisions</Option>
            <Option value="others">Others</Option>
          </Select>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Benefit Type
          </label>
          <Select
            className="custom-select"
            size="large"
            placeholder="Select benefit type"
            loading={isLoading}
            value={benefitPlan?.benefit_type}
            onChange={value => handleSelectChange("benefit_type", value)}
          >
            {sortedByName(planTypes?.data)?.map((item: any) => (
              <Option
                key={item.id}
                value={item.name === "Cash" ? "cash" : "non_cash"}
              >
                {item?.name}
              </Option>
            ))}
          </Select>
        </Col>
        {benefitPlan?.benefit_type === "cash" && (
          <Col span={12}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Calculation Basis
            </label>
            <Select
              className="custom-select"
              size="large"
              value={benefitPlan?.calculation_basis}
              onChange={value => handleSelectChange("calculation_basis", value)}
            >
              <Option value="percent_basic">% of Basic</Option>
              <Option value="percent_guaranteed">% of Guaranteed Cash</Option>
              <Option value="percent_actual">% of Actual</Option>
              <Option value="flat">Flat Amount</Option>
            </Select>
          </Col>
        )}
      </Row>

      {benefitPlan?.benefit_type === "cash" ? (
        <>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
                Value
              </label>
              <Input
                className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                size="large"
                name="value"
                placeholder="Value"
                value={formatNumberWithCommas(benefitPlan?.value)}
                onChange={handleChange}
              />
            </Col>
          </Row>
        </>
      ) : (
        <></>
      )}
      {benefitPlan?.benefit_type === "non_cash" && (
        <>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
                Benefit Value
              </label>
              <Input
                className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                size="large"
                name="value"
                placeholder="Benefit Value"
                value={formatNumberWithCommas(benefitPlan?.value)}
                onChange={handleChange}
              />
            </Col>
            <Col span={14} className="mt-2">
              <Checkbox
                name="include_in_offer_detail"
                checked={benefitPlan?.include_in_offer_detail}
                onChange={handleChange}
              >
                Include in Offer Detail
              </Checkbox>

              <br />

              <Checkbox
                className="mt-1"
                name="include_in_total_remuneration"
                checked={benefitPlan?.include_in_total_remuneration}
                onChange={handleChange}
              >
                Include in Total Remuneration
              </Checkbox>

            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
                Cost to Employer
              </label>
              <Input
                className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                size="large"
                name="employer_cost"
                placeholder="Cost to Employer"
                value={formatNumberWithCommas(benefitPlan?.employer_cost)}
                onChange={handleChange}
              />
            </Col>
          </Row>
        </>
      )}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Grades
          </label>

          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            placeholder="Grades"
            disabled={benefitPlan?.is_all_grade}
            value={benefitPlan?.grade?.length ? benefitPlan.grade : undefined}
            onChange={values => {
              if (values?.includes(ALL_GRADES_VALUE)) {
                setBenefitPlan(prev => ({
                  ...prev,
                  country_id: null, // reset selected country if grades change
                  is_global: false,
                }));
                handleGradeChange(
                  Array.from(new Set(jobGrades?.map((jg: any) => jg?.grade))) ||
                  []
                );
              } else {
                handleGradeChange(values);
              }
            }}
          >
            {benefitPlan?.grade?.length !==
              [...new Set(jobGrades?.map(jgd => jgd?.grade)?.filter(Boolean))]
                ?.length && (
                <Option key={ALL_GRADES_VALUE} value={ALL_GRADES_VALUE}>
                  All Grades
                </Option>
              )}
            {[
              ...new Set(jobGrades?.map(jgd => jgd?.grade)?.filter(Boolean)),
            ].map(jg => (
              <Option key={jg} value={jg}>
                {jg}
              </Option>
            ))}
          </Select>

          <Row style={{ marginTop: 16 }}></Row>
        </Col>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Country
          </label>
          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            disabled={gradeCountries?.length === 0}
            placeholder="Grades determine available countries"
            value={benefitPlan?.country_id || undefined}
            onChange={values => {
              if (values?.includes(ALL_COUNTRIES)) {
                handleGlobalChange(true);
              } else {
                handleSelectChange("country_id", values);
              }
            }}
          >
            {benefitPlan?.country_id?.length !== gradeCountries?.length && (
              <Option key={ALL_COUNTRIES} value={ALL_COUNTRIES}>
                All Countries
              </Option>
            )}

            {sortedByName(gradeCountries)?.map(c => (
              <Option key={c?.id} value={c?.id}>
                {c?.name}
              </Option>
            ))}
          </Select>

          <Row style={{ marginTop: 16 }}></Row>
        </Col>

        {/* new field business unit */}
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Business Units
          </label>
          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            disabled={businessUnits?.length === 0}
            placeholder="Business Units"
            value={benefitPlan?.business_unit_ids || undefined}
            onChange={values => handleSelectChange("business_unit_ids", values)}
          >
            {businessUnits.map((bu: any) => (
              <Option key={bu.id} value={bu.id}>
                {bu.name}
              </Option>
            ))}
          </Select>

          <Row style={{ marginTop: 16 }}></Row>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            City
          </label>
          <Input
            className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
            size="large"
            name="city"
            placeholder="City"
            value={benefitPlan?.city}
            onChange={handleChange}
          />
        </Col>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Description
          </label>
          <Input.TextArea
            className={`w-full resize shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
            name="description"
            placeholder="Describe the benefit plan..."
            value={benefitPlan?.description}
            onChange={handleChange}
          />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card bordered={false} style={{ background: "#fafafa" }}>
            <Text strong>Policy Manual</Text>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 8 }}
            >
              Upload PDF or DOC/DOCX file
            </Text>

            <Upload
              beforeUpload={file => {
                const fileExtension = file.name.split(".").pop()?.toLowerCase();
                const allowedExtensions = ["pdf", "doc", "docx"];

                if (
                  !fileExtension ||
                  !allowedExtensions.includes(fileExtension)
                ) {
                  customErrorMessage(
                    "You can only upload PDF or DOC/DOCX files!"
                  );
                  return Upload.LIST_IGNORE;
                }

                handleUpload(file);
                return false; // stop auto upload but keep valid file
              }}
              onRemove={() => {
                setBenefitPlan(prev => ({ ...prev, policy_manual: null }));
              }}
              maxCount={1}
            >
              <button
                className={`bg-themeBlue text-white font-medium text-lg py-1 px-6 rounded-md `}
              >
                <UploadOutlined /> Upload File
              </button>
            </Upload>
          </Card>
        </Col>
      </Row>
    </CustomModal>
  );
};

export default AddBenefitsPlan;
